import { useCallback, useState, type ReactNode } from 'react';
import type {
  ProcessDraftAssignment,
  ProcessDraftStep,
  ProcessStepRecordingDisplay
} from '@bliks-io/contracts';
import { DraftStepPanel } from '../panels/DraftStepPanel';
import { DraftEventPanel } from '../panels/DraftEventPanel';
import { DraftUnclearSuccessorPanel } from '../panels/DraftUnclearSuccessorPanel';
import type { CanvasSelectionTarget } from '@bliks-io/ui';
import type { DraftEditorEvent } from '../draftEvents';

type DetailTarget = Exclude<CanvasSelectionTarget, { kind: 'none' }>;

interface Args {
  processId: string;
  steps: ProcessDraftStep[];
  draftAssignments: ProcessDraftAssignment[];
  displayedSteps: ProcessStepRecordingDisplay[];
  draftEvents: DraftEditorEvent[];
}

/**
 * Packages the controlled-selection state + the detail renderers
 * `ProcessFlowEditor` expects. Extracted from `DraftEditorPage` to keep the
 * inner component under its line-count lint threshold.
 *
 * The start event resolves to an `event` target like every other event node
 * (ADR 0003 P3b) — `DraftEventPanel` switches the type choices and copy on the
 * event's `eventKind`.
 */
export function useDraftDetailRenderer({
  processId,
  steps,
  draftAssignments,
  displayedSteps,
  draftEvents
}: Args) {
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  // Anchor for suggestions raised from the assignment-side panels: a draft
  // assignment never becomes a recording node, while the start event's stable
  // id survives publish as the live start node's `_id`.
  const startEventStableStepId = draftEvents.find((ev) => ev.eventKind === 'start')?._id;

  const renderDetail = useCallback(
    (target: DetailTarget, onClose: () => void): ReactNode => {
      const close = () => {
        setSelectedStepId(null);
        onClose();
      };
      switch (target.kind) {
        case 'step':
        case 'placeholder': {
          // A recorded step and an open-assignment placeholder are both keyed
          // by `stableStepId`; `DraftStepPanel` dispatches on which it got.
          const stableStepId = target.kind === 'step' ? target.step._id : target.stableStepId;
          return (
            <DraftStepPanel
              step={steps.find((s) => String(s.stableStepId) === stableStepId)}
              assignment={draftAssignments.find((a) => String(a.stableStepId) === stableStepId)}
              processId={processId}
              startEventStableStepId={startEventStableStepId}
              siblingSteps={displayedSteps}
              onClose={close}
            />
          );
        }
        case 'event': {
          const event = draftEvents.find((e) => e._id === target.eventId);
          if (!event) return null;
          // Keyed per event, like `DraftStepPanel`: a reused instance would carry
          // the previous event's debounced edits and field focus over.
          return (
            <DraftEventPanel key={event._id} processId={processId} event={event} onClose={close} />
          );
        }
        case 'unclear-end':
          return (
            <DraftUnclearSuccessorPanel
              processId={processId}
              startEventStableStepId={startEventStableStepId}
              predecessorStepId={target.predecessorStepId}
              branchKey={target.branchKey}
              onClose={close}
            />
          );
      }
    },
    [steps, draftAssignments, processId, displayedSteps, draftEvents, startEventStableStepId]
  );

  return { selectedStepId, setSelectedStepId, renderDetail };
}
